import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { Skeleton } from '@/components/feedback/skeleton'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { Select } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { Textarea } from '@/components/forms/textarea'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { PageHeader } from '@/components/layout/page-header'
import { Drawer } from '@/components/overlays/drawer'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import {
  DrawerStepProgress,
  DrawerStepTitle,
  DrawerTabs,
  DrawerWizardFooter,
} from '@/components/overlays/drawer-wizard'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import oauth2LogoUrl from '@/assets/sso-providers/oauth2.svg'
import oidcLogoUrl from '@/assets/sso-providers/oidc.svg'
import samlLogoUrl from '@/assets/sso-providers/saml.svg'
import casLogoUrl from '@/assets/sso-providers/cas.svg'
import { cn } from '@/lib/cn'

import {
  getAdminSsoConfig,
  testAdminSsoConfig,
  updateAdminSsoConfig,
  type AdminSsoConfig,
  type SsoProtocolConfig,
  type SsoType,
} from './api'

interface SsoFieldDefinition {
  key: string
  multiline?: boolean
  required?: boolean
  secret?: boolean
}

type SsoDrawerMode = 'create' | 'edit'
type SsoDrawerSection = 'connection' | 'identity'

const ssoDrawerSections = ['connection', 'identity'] as const

const providerOrder: SsoType[] = ['oidc', 'saml', 'cas', 'oauth2']
const providerLogos: Record<SsoType, string> = {
  cas: casLogoUrl,
  oauth2: oauth2LogoUrl,
  oidc: oidcLogoUrl,
  saml: samlLogoUrl,
}
const fieldsByProvider: Record<SsoType, SsoFieldDefinition[]> = {
  cas: [
    { key: 'cas_base_url', required: true },
    { key: 'cas_match_attribute', required: true },
  ],
  oauth2: [
    { key: 'authorization_url', required: true },
    { key: 'token_url', required: true },
    { key: 'userinfo_url', required: true },
    { key: 'logout_url' },
    { key: 'logout_redirect_url' },
    { key: 'scope' },
    { key: 'client_id', required: true },
    { key: 'client_secret', required: true, secret: true },
    { key: 'oauth2_subject_claim', required: true },
  ],
  oidc: [
    { key: 'issuer', required: true },
    { key: 'authorization_endpoint', required: true },
    { key: 'token_endpoint', required: true },
    { key: 'userinfo_endpoint', required: true },
    { key: 'end_session_endpoint' },
    { key: 'algorithm' },
    { key: 'jwks_uri' },
    { key: 'scope' },
    { key: 'client_id', required: true },
    { key: 'client_secret', required: true, secret: true },
  ],
  saml: [
    { key: 'idp_entity_id', required: true },
    { key: 'idp_sso_url', required: true },
    { key: 'idp_x509_cert', multiline: true, required: true, secret: true },
    { key: 'sp_entity_id', required: true },
    { key: 'acs_url', required: true },
    { key: 'idp_slo_url' },
    { key: 'sp_x509_cert', multiline: true, secret: true },
    { key: 'sp_private_key', multiline: true, secret: true },
  ],
}

const identityFieldsByProvider: Record<SsoType, SsoFieldDefinition[]> = {
  cas: [
    { key: 'provisioning_policy', required: true },
    { key: 'identity_mapping_source', required: true },
    { key: 'identity_mapping_target', required: true },
  ],
  oauth2: [
    { key: 'provisioning_policy', required: true },
    { key: 'identity_mapping_source', required: true },
    { key: 'identity_mapping_target', required: true },
  ],
  oidc: [
    { key: 'provisioning_policy', required: true },
    { key: 'identity_mapping_source', required: true },
    { key: 'identity_mapping_target', required: true },
  ],
  saml: [
    { key: 'provisioning_policy', required: true },
    { key: 'identity_mapping_source', required: true },
    { key: 'identity_mapping_target', required: true },
  ],
}

const sourcePresetsByProvider: Record<SsoType, string[]> = {
  cas: ['user', 'email'],
  oauth2: ['email', 'username', 'userPrincipalName'],
  oidc: ['email', 'preferred_username', 'sub'],
  saml: ['email', 'name_id', 'uid'],
}

const sourceDisplayNames: Record<string, string> = {
  name_id: 'NameID',
}

function defaultIdentityConfig(type: SsoType): SsoProtocolConfig {
  if (type === 'cas') {
    return {
      cas_match_attribute: 'external_person_id',
      identity_mapping_source: 'user',
      identity_mapping_target: 'external_person_id',
      provisioning_policy: 'preprovisioned',
    }
  }

  return {
    identity_mapping_source: 'email',
    identity_mapping_target: 'email',
    provisioning_policy: 'preprovisioned',
    ...(type === 'oauth2' ? { oauth2_subject_claim: 'id' } : {}),
  }
}

function configFieldsForProvider(type: SsoType) {
  return [...fieldsByProvider[type], ...identityFieldsByProvider[type]]
}

function readConfigString(values: SsoProtocolConfig, key: string) {
  const value = values[key]
  return typeof value === 'string' ? value.trim() : ''
}

function hasFieldValue(values: SsoProtocolConfig, field: SsoFieldDefinition) {
  if (readConfigString(values, field.key)) {
    return true
  }

  return field.secret ? Boolean(readConfigString(values, `masked_${field.key}`)) : false
}

function isProviderComplete(type: SsoType | null, values: SsoProtocolConfig) {
  if (!type) {
    return false
  }

  return configFieldsForProvider(type)
    .filter((field) => field.required)
    .every((field) => hasFieldValue(values, field))
}

function buildProtocolPayload(
  type: SsoType,
  values: SsoProtocolConfig,
  initialValues: SsoProtocolConfig,
) {
  return configFieldsForProvider(type).reduce<SsoProtocolConfig>((payload, field) => {
    const value = readConfigString(values, field.key)
    if (value) {
      payload[field.key] = value
      return payload
    }

    if (field.secret) {
      return payload
    }

    if (readConfigString(initialValues, field.key)) {
      payload[field.key] = null
    }
    return payload
  }, {})
}

export function AdminSsoSettingsPage() {
  const { t } = useTranslation('adminSettings')
  const ssoQuery = useQuery({
    queryFn: getAdminSsoConfig,
    queryKey: ['admin-sso-config'],
  })
  const isError = ssoQuery.isError
  const isLoading = ssoQuery.isLoading || !ssoQuery.data

  return (
    <AdminShellPage activePath="/admin/settings/sso">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          contentClassName="max-w-[820px]"
          description={t('sso.description')}
          title={t('sso.title')}
        />
        {isError ? (
          <div className="flex flex-1 items-center justify-center px-6">
            <ErrorState
              description={t('sso.loadDescription')}
              onRetry={() => {
                void ssoQuery.refetch()
              }}
              title={t('sso.loadTitle')}
            />
          </div>
        ) : isLoading ? (
          <div className="px-5 py-5 lg:px-[30px]">
            <PageContentBoundary className="grid max-w-[820px] gap-5">
              <Skeleton className="h-28 rounded-xl" />
              <Skeleton className="h-48 rounded-xl" />
              <Skeleton className="h-36 rounded-xl" />
            </PageContentBoundary>
          </div>
        ) : (
          <AdminSsoSettingsContent
            key={`${ssoQuery.data.sso_type ?? 'none'}:${ssoQuery.data.updated_at ?? ''}`}
            settings={ssoQuery.data}
          />
        )}
      </div>
    </AdminShellPage>
  )
}

function AdminSsoSettingsContent({ settings }: { settings: AdminSsoConfig }) {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [currentSettings, setCurrentSettings] = useState(settings)
  const [editingProvider, setEditingProvider] = useState<SsoType | null>(null)
  const activeProviderComplete =
    currentSettings.sso_type === null
      ? false
      : isProviderComplete(currentSettings.sso_type, currentSettings.sso_config)
  const providerActions = useMemo(
    () =>
      providerOrder.map((type) => ({
        logo: providerLogos[type],
        title: t(`sso.providers.${type}.title`),
        type,
      })),
    [t],
  )

  const saveMutation = useMutation({
    mutationFn: updateAdminSsoConfig,
    onError: () => {
      showFeedback({
        message: t('errors.submitFailed'),
        tone: 'error',
      })
    },
    onSuccess: async (result) => {
      setCurrentSettings(result)
      await queryClient.invalidateQueries({ queryKey: ['admin-sso-config'] })
      showFeedback({
        message: t('success.updateSsoSettings'),
        tone: 'success',
      })
    },
  })
  const testMutation = useMutation({
    mutationFn: testAdminSsoConfig,
    onError: () => {
      showFeedback({
        message: t('sso.test.failed'),
        tone: 'error',
      })
    },
    onSuccess: (result) => {
      showFeedback({
        message: result.status === 'warning' ? t('sso.test.warning') : t('sso.test.success'),
        tone: result.status === 'warning' ? 'warning' : 'success',
      })
    },
  })

  const saveSettingsPatch = (patch: Partial<AdminSsoConfig>) => {
    const next = {
      ...currentSettings,
      ...patch,
    }
    saveMutation.mutate({
      email_password_enabled: next.email_password_enabled,
      sso_login_behavior: next.sso_login_behavior,
      sso_type: next.sso_type,
      web_sso_enabled: next.web_sso_enabled,
    })
  }
  const handleSsoToggle = (checked: boolean) => {
    if (checked && !activeProviderComplete) {
      showFeedback({
        message: t('sso.validation.configRequired'),
        tone: 'error',
      })
      return
    }
    if (!checked && !currentSettings.email_password_enabled) {
      showFeedback({
        message: t('sso.validation.oneLoginRequired'),
        tone: 'error',
      })
      return
    }

    saveSettingsPatch({
      sso_login_behavior: checked ? currentSettings.sso_login_behavior : 'button',
      web_sso_enabled: checked,
    })
  }
  const handleEmailToggle = (checked: boolean) => {
    if (!checked && !currentSettings.web_sso_enabled) {
      showFeedback({
        message: t('sso.validation.oneLoginRequired'),
        tone: 'error',
      })
      return
    }

    saveSettingsPatch({ email_password_enabled: checked })
  }
  const handleAutoRedirectToggle = (checked: boolean) => {
    if (checked && !currentSettings.web_sso_enabled) {
      showFeedback({
        message: t('sso.validation.redirectRequiresSso'),
        tone: 'error',
      })
      return
    }

    saveSettingsPatch({ sso_login_behavior: checked ? 'redirect' : 'button' })
  }
  const saveProviderConfig = (type: SsoType, values: SsoProtocolConfig) => {
    saveMutation.mutate(
      {
        email_password_enabled: currentSettings.email_password_enabled,
        sso_login_behavior: currentSettings.sso_login_behavior,
        sso_config: values,
        sso_type: type,
        web_sso_enabled: currentSettings.web_sso_enabled,
      },
      {
        onSuccess: () => setEditingProvider(null),
      },
    )
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-[30px]">
        <PageContentBoundary className="grid max-w-[820px] gap-5">
          <section className="grid gap-5 rounded-lg border border-[var(--color-border)] bg-white p-5 shadow-[0_1px_2px_rgba(26,28,25,0.04)]">
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
              <div className="min-w-0">
                <h2 className="text-base font-bold text-[var(--color-text)]">
                  {t('sso.providersTitle')}
                </h2>
                <p className="mt-2 text-sm leading-6 text-[var(--color-text-muted)]">
                  {t('sso.providersDescription')}
                </p>
              </div>
              <div className="flex shrink-0 sm:justify-end">
                <Button
                  disabled={!currentSettings.web_sso_enabled || testMutation.isPending}
                  isLoading={testMutation.isPending}
                  onClick={() => testMutation.mutate()}
                  size="sm"
                  variant="secondary"
                >
                  {t('sso.actions.test')}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {providerActions.map((provider) => {
                const isActive = currentSettings.sso_type === provider.type
                const configured =
                  isActive && isProviderComplete(provider.type, currentSettings.sso_config)
                const actionLabel = t(
                  isActive ? 'sso.actions.editProvider' : 'sso.actions.configureProvider',
                  { provider: provider.title },
                )
                return (
                  <button
                    aria-label={actionLabel}
                    aria-pressed={isActive}
                    className={cn(
                      'inline-flex h-11 min-w-[150px] cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 text-sm font-semibold transition-colors',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                      isActive
                        ? 'border-[rgba(25,91,255,0.32)] bg-[#eef4ff] text-[var(--color-primary)]'
                        : 'border-transparent bg-[#e9ebf1] text-[#11142d] hover:bg-[#dfe3eb]',
                    )}
                    key={provider.type}
                    onClick={() => setEditingProvider(provider.type)}
                    type="button"
                  >
                    <img alt="" className="size-5 shrink-0" src={provider.logo} />
                    <span className="truncate">{actionLabel}</span>
                    {configured ? (
                      <span
                        aria-label={t('sso.status.configured')}
                        className="size-1.5 shrink-0 rounded-full bg-[var(--color-primary)]"
                      />
                    ) : null}
                  </button>
                )
              })}
            </div>
          </section>

          <section className="grid gap-5 rounded-lg border border-[var(--color-border)] bg-white p-5 shadow-[0_1px_2px_rgba(26,28,25,0.04)]">
            <div>
              <h2 className="text-base font-bold text-[var(--color-text)]">
                {t('sso.methodsTitle')}
              </h2>
              <p className="mt-1 text-sm leading-6 text-[var(--color-text-muted)]">
                {t('sso.methodsDescription')}
              </p>
            </div>
            <div className="grid max-w-[740px] gap-6">
              <MethodSwitchRow
                checked={currentSettings.web_sso_enabled}
                disabled={saveMutation.isPending}
                description={t('sso.enableSsoDescription')}
                onCheckedChange={handleSsoToggle}
                title={t('sso.enableSso')}
              />
              <MethodSwitchRow
                checked={currentSettings.email_password_enabled}
                disabled={saveMutation.isPending}
                description={t('sso.enableEmailDescription')}
                onCheckedChange={handleEmailToggle}
                title={t('sso.enableEmail')}
              />
              <MethodSwitchRow
                checked={currentSettings.sso_login_behavior === 'redirect'}
                disabled={!currentSettings.web_sso_enabled || saveMutation.isPending}
                description={t('sso.autoRedirectDescription')}
                onCheckedChange={handleAutoRedirectToggle}
                title={t('sso.autoRedirect')}
              />
            </div>
          </section>
        </PageContentBoundary>
      </div>

      {editingProvider ? (
        <SsoProviderDrawer
          initialValues={
            currentSettings.sso_type === editingProvider ? currentSettings.sso_config : {}
          }
          isSaving={saveMutation.isPending}
          mode={currentSettings.sso_type === editingProvider ? 'edit' : 'create'}
          onClose={() => setEditingProvider(null)}
          onSubmit={(values) => saveProviderConfig(editingProvider, values)}
          type={editingProvider}
        />
      ) : null}
    </>
  )
}

function MethodSwitchRow({
  checked,
  description,
  disabled,
  onCheckedChange,
  title,
}: {
  checked: boolean
  description: string
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
  title: string
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-5">
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-[var(--color-text)]">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-[var(--color-text)]">{description}</p>
      </div>
      <Switch
        aria-label={title}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  )
}

function SsoProviderDrawer({
  initialValues,
  isSaving,
  mode,
  onClose,
  onSubmit,
  type,
}: {
  initialValues: SsoProtocolConfig
  isSaving: boolean
  mode: SsoDrawerMode
  onClose: () => void
  onSubmit: (values: SsoProtocolConfig) => void
  type: SsoType
}) {
  const { t } = useTranslation('adminSettings')
  const [values, setValues] = useState<SsoProtocolConfig>(() => ({
    ...defaultIdentityConfig(type),
    ...initialValues,
  }))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [section, setSection] = useState<SsoDrawerSection>('connection')
  const fields = fieldsByProvider[type]

  const updateField = (field: string, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: '' }))
  }
  const validateFields = (targetFields: SsoFieldDefinition[]) => {
    const nextErrors = Object.fromEntries(
      targetFields
        .filter((field) => field.required && !hasFieldValue(values, field))
        .map((field) => [field.key, t('sso.validation.required')]),
    )
    if (Object.keys(nextErrors).length > 0) {
      setErrors((current) => ({ ...current, ...nextErrors }))
      return false
    }
    return true
  }

  const submit = () => {
    const connectionValid = validateFields(fields)
    const identityValid = validateFields(identityFieldsByProvider[type])
    if (!connectionValid || !identityValid) {
      setSection(connectionValid ? 'identity' : 'connection')
      return
    }

    setErrors({})
    onSubmit(buildProtocolPayload(type, values, initialValues))
  }

  const headerAddon =
    mode === 'create' ? (
      <DrawerStepProgress
        ariaLabel={t('sso.flow.progressLabel')}
        canSelectStep={() => true}
        className="w-full"
        currentStep={section}
        getStepLabel={(item) => t(`sso.flow.sections.${item}`)}
        onStepChange={setSection}
        steps={ssoDrawerSections}
      />
    ) : (
      <DrawerTabs
        activeTab={section}
        ariaLabel={t('sso.flow.tabsLabel')}
        onTabChange={setSection}
        tabs={ssoDrawerSections.map((item) => ({
          id: item,
          label: t(`sso.flow.sections.${item}`),
          panelId: `sso-${type}-${item}-panel`,
          tabId: `sso-${type}-${item}-tab`,
        }))}
      />
    )

  const footer =
    mode === 'create' ? (
      <DrawerWizardFooter
        backLabel={t('sso.flow.previous')}
        canGoBack={section === 'identity'}
        isLoading={isSaving}
        nextLabel={section === 'connection' ? t('sso.flow.next') : t('actions.save')}
        onBack={() => setSection('connection')}
        onNext={() => {
          if (section === 'connection') {
            setSection('identity')
            return
          }
          submit()
        }}
      />
    ) : (
      <>
        <Button onClick={onClose} variant="secondary">
          {t('actions.cancel')}
        </Button>
        <Button isLoading={isSaving} onClick={submit}>
          {t('actions.save')}
        </Button>
      </>
    )

  return (
    <Drawer
      footer={footer}
      headerAddon={headerAddon}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      open
      size="lg"
      title={t('sso.drawer.title', { provider: t(`sso.providers.${type}.title`) })}
    >
      <div
        aria-labelledby={mode === 'edit' ? `sso-${type}-${section}-tab` : undefined}
        id={`sso-${type}-${section}-panel`}
        role={mode === 'edit' ? 'tabpanel' : undefined}
      >
        {section === 'connection' ? (
          <SsoConnectionSection
            errors={errors}
            fields={fields}
            onFieldChange={updateField}
            type={type}
            values={values}
          />
        ) : (
          <SsoIdentitySection
            errors={errors}
            onFieldChange={updateField}
            type={type}
            values={values}
          />
        )}
      </div>
    </Drawer>
  )
}

function SsoConnectionSection({
  errors,
  fields,
  onFieldChange,
  type,
  values,
}: {
  errors: Record<string, string>
  fields: SsoFieldDefinition[]
  onFieldChange: (field: string, value: string) => void
  type: SsoType
  values: SsoProtocolConfig
}) {
  const { t } = useTranslation('adminSettings')

  return (
    <div className="grid gap-4">
      <DrawerStepTitle title={t('sso.flow.connectionTitle')} />
      <p className="-mt-3 text-sm leading-6 text-[var(--color-text-muted)]">
        {t(`sso.providers.${type}.configuration`)}
      </p>
      {fields
        .filter((field) => field.key !== 'cas_match_attribute')
        .map((field) => {
          const fieldId = `sso-${type}-${field.key}`
          const value = values[field.key] ?? ''
          const maskedSecretValue = field.secret
            ? readConfigString(values, `masked_${field.key}`)
            : ''
          const placeholder =
            field.secret && maskedSecretValue
              ? t('sso.placeholders.secretWithSaved', { value: maskedSecretValue })
              : t(`sso.placeholders.${field.key}`)
          return (
            <FormField
              error={errors[field.key]}
              htmlFor={fieldId}
              key={field.key}
              label={t(`sso.fields.${field.key}`)}
              required={field.required}
            >
              {field.multiline ? (
                <Textarea
                  id={fieldId}
                  invalid={Boolean(errors[field.key])}
                  onChange={(event) => onFieldChange(field.key, event.target.value)}
                  placeholder={placeholder}
                  rows={5}
                  value={value}
                />
              ) : field.secret ? (
                <PasswordInput
                  id={fieldId}
                  invalid={Boolean(errors[field.key])}
                  onChange={(event) => onFieldChange(field.key, event.target.value)}
                  placeholder={placeholder}
                  value={value}
                />
              ) : (
                <Input
                  id={fieldId}
                  invalid={Boolean(errors[field.key])}
                  onChange={(event) => onFieldChange(field.key, event.target.value)}
                  placeholder={placeholder}
                  value={value}
                />
              )}
            </FormField>
          )
        })}
    </div>
  )
}

function SsoIdentitySection({
  errors,
  onFieldChange,
  type,
  values,
}: {
  errors: Record<string, string>
  onFieldChange: (field: string, value: string) => void
  type: SsoType
  values: SsoProtocolConfig
}) {
  const { t } = useTranslation('adminSettings')
  const mappingSource = readConfigString(values, 'identity_mapping_source')
  const mappingTarget = readConfigString(values, 'identity_mapping_target')
  const provisioningPolicy = readConfigString(values, 'provisioning_policy')
  const sourcePresets = sourcePresetsByProvider[type]
  const supportsCustomSource = type !== 'cas'
  const usesCustomSource = supportsCustomSource && !sourcePresets.includes(mappingSource)
  const sourceOptions = [
    ...sourcePresets.map((source) => ({
      label: `${t(`sso.providers.${type}.title`)}: ${sourceDisplayNames[source] ?? source}`,
      value: source,
    })),
    ...(supportsCustomSource
      ? [
          {
            label: `${t(`sso.providers.${type}.title`)}: ${t('sso.bridge.customSource')}`,
            value: '__custom__',
          },
        ]
      : []),
  ]

  return (
    <div className="grid gap-5">
      <DrawerStepTitle compact title={t('sso.flow.identityTitle')} />

      <section className="grid gap-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-bold text-[var(--color-text)]">
            {t('sso.bridge.mappingTitle')}
          </h4>
          <HelpTooltip label={t('sso.bridge.mappingHelp')} widthClassName="w-[320px]">
            {t('sso.bridge.mappingHelp')}
          </HelpTooltip>
        </div>

        <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <FormField
            error={errors.identity_mapping_source}
            htmlFor={`sso-${type}-identity-mapping-source`}
            label={t('sso.bridge.externalField')}
            required
          >
            <Select
              id={`sso-${type}-identity-mapping-source`}
              invalid={Boolean(errors.identity_mapping_source)}
              onValueChange={(value) => {
                onFieldChange(
                  'identity_mapping_source',
                  value === '__custom__' ? (usesCustomSource ? mappingSource : '') : value,
                )
              }}
              options={sourceOptions}
              value={usesCustomSource ? '__custom__' : mappingSource}
            />
          </FormField>
          <span aria-hidden className="pb-2.5 text-center text-[var(--color-text-muted)]">
            →
          </span>
          <FormField
            error={errors.identity_mapping_target}
            htmlFor={`sso-${type}-identity-mapping-target`}
            label={t('sso.bridge.hubField')}
            required
          >
            <Select
              id={`sso-${type}-identity-mapping-target`}
              invalid={Boolean(errors.identity_mapping_target)}
              onValueChange={(value) => onFieldChange('identity_mapping_target', value)}
              options={[
                { label: t('sso.bridge.targets.email'), value: 'email' },
                {
                  label: t('sso.bridge.targets.external_person_id'),
                  value: 'external_person_id',
                },
              ]}
              value={mappingTarget}
            />
          </FormField>
        </div>
        {usesCustomSource ? (
          <FormField
            error={errors.identity_mapping_source}
            htmlFor={`sso-${type}-custom-mapping-source`}
            hint={t('sso.bridge.customSourceHint')}
            label={t('sso.bridge.customSourceField')}
            required
          >
            <Input
              id={`sso-${type}-custom-mapping-source`}
              invalid={Boolean(errors.identity_mapping_source)}
              onChange={(event) => onFieldChange('identity_mapping_source', event.target.value)}
              placeholder={t('sso.bridge.customSourcePlaceholder')}
              value={mappingSource}
            />
          </FormField>
        ) : null}
      </section>

      <section className="border-t border-[var(--color-divider)] pt-5">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-5">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-bold text-[var(--color-text)]">{t('sso.jit.title')}</h4>
            <HelpTooltip label={t(`sso.jit.help.${type}`)} widthClassName="w-[320px]">
              {t(`sso.jit.help.${type}`)}
            </HelpTooltip>
          </div>
          <Switch
            aria-label={t('sso.jit.title')}
            checked={provisioningPolicy === 'jit'}
            onCheckedChange={(checked) =>
              onFieldChange('provisioning_policy', checked ? 'jit' : 'preprovisioned')
            }
          />
        </div>
      </section>
    </div>
  )
}
